import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useState } from 'react';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const FORM_URL = 'https://readdy.ai/api/form/d96v47f0d76aer3t58n0';

export default function ContactPage() {
  const { ref: heroRef, isVisible: heroVisible } = useScrollReveal(0.1);
  const { ref: contentRef, isVisible: contentVisible } = useScrollReveal(0.1);

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot.length > 0) {
      setSubmitted(true);
      setSubmitting(false);
      return;
    }

    const name = (formData.get('name') as string || '').trim();
    const email = (formData.get('email') as string || '').trim();
    const message = (formData.get('message') as string || '').trim();

    if (!name || !email || !message) {
      setError('Please fill in all required fields (name, email, message).');
      setSubmitting(false);
      return;
    }

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== 'phone_alt') {
        params.append(key, value as string);
      }
    });

    try {
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSuccess = response.ok && parsed?.code === 'OK';

      if (isSuccess) {
        setSubmitted(true);
        form.reset();
      } else {
        setError(serverMsg || 'Something went wrong. Please try again later.');
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    }

    setSubmitting(false);
  };

  return (
    <main>
      <Navbar />
      <div className="h-20"></div>

      {/* Hero */}
      <section className="relative bg-primary-900 py-20 md:py-28 px-6 md:px-10 lg:px-16 overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-primary-700/40 blur-3xl -translate-y-1/2 translate-x-1/4 pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-accent-500/10 blur-3xl translate-y-1/2 -translate-x-1/4 pointer-events-none"></div>

        <div
          ref={heroRef}
          className={`relative max-w-4xl mx-auto text-center transition-all duration-700 ${
            heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-background-50/15 text-background-50/80 text-xs font-bold tracking-widest uppercase mb-6">
            Let&apos;s Talk
          </span>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-[1.1] mb-5">
            Ready to Build Your{' '}
            <span className="text-accent-300 italic">Dream Team?</span>
          </h1>
          <p className="text-background-50/65 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
            Whether you need a single executive hire or a complete recruitment strategy, we&apos;re ready to partner with you. Let&apos;s discuss how we can help.
          </p>
        </div>
      </section>

      {/* Contact Info + Form */}
      <section className="relative bg-background-50 py-24 md:py-32 px-6 md:px-10 lg:px-16">
        <div
          ref={contentRef}
          className={`max-w-7xl mx-auto transition-all duration-700 ${
            contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Left - Contact Cards */}
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                Get in Touch
              </h2>
              <p className="text-foreground-500 text-base leading-relaxed mb-8">
                Have a question or want to discuss your hiring needs? Reach out to us directly or fill out the form and we will get back to you promptly.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                <a
                  href="tel:+918447691414"
                  className="group flex flex-col items-start gap-3 p-6 rounded-2xl bg-background-100 border border-background-200 hover:border-accent-300 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center group-hover:bg-accent-200 transition-colors">
                    <i className="ri-phone-fill text-accent-500 text-xl"></i>
                  </div>
                  <div>
                    <p className="text-foreground-400 text-xs font-medium tracking-widest uppercase mb-1">
                      Call Us
                    </p>
                    <p className="text-foreground-900 font-bold text-sm">+91 8447691414</p>
                  </div>
                </a>

                <a
                  href="tel:+919799374635"
                  className="group flex flex-col items-start gap-3 p-6 rounded-2xl bg-background-100 border border-background-200 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center group-hover:bg-primary-200 transition-colors">
                    <i className="ri-phone-fill text-primary-500 text-xl"></i>
                  </div>
                  <div>
                    <p className="text-foreground-400 text-xs font-medium tracking-widest uppercase mb-1">
                      Call Us
                    </p>
                    <p className="text-foreground-900 font-bold text-sm">+91 9799374635</p>
                  </div>
                </a>

                <a
                  href="mailto:info@jobmanifest.in"
                  className="group flex flex-col items-start gap-3 p-6 rounded-2xl bg-background-100 border border-background-200 hover:border-accent-300 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center group-hover:bg-accent-200 transition-colors">
                    <i className="ri-mail-line text-accent-500 text-xl"></i>
                  </div>
                  <div>
                    <p className="text-foreground-400 text-xs font-medium tracking-widest uppercase mb-1">
                      Email Us
                    </p>
                    <p className="text-foreground-900 font-bold text-sm">info@jobmanifest.in</p>
                  </div>
                </a>

                <div className="flex flex-col items-start gap-3 p-6 rounded-2xl bg-background-100 border border-background-200">
                  <div className="w-12 h-12 rounded-xl bg-secondary-100 flex items-center justify-center">
                    <i className="ri-map-pin-line text-secondary-500 text-xl"></i>
                  </div>
                  <div>
                    <p className="text-foreground-400 text-xs font-medium tracking-widest uppercase mb-1">
                      Visit Us
                    </p>
                    <p className="text-foreground-900 font-bold text-sm">
                      B-37, Sector 63, Noida - 201301
                    </p>
                  </div>
                </div>
              </div>

              {/* Map embed */}
              <div className="rounded-2xl overflow-hidden border border-background-200 aspect-[16/9]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3502.3155637!2d77.3652!3d28.6269!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjjCsDM3JzM2LjgiTiA3N8KwMjEnNTQuNyJF!5e0!3m2!1sen!2sin!4v1699999999999"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Job Manifest Office Location"
                  className="w-full h-full"
                ></iframe>
              </div>
            </div>

            {/* Right - Form */}
            <div>
              {submitted ? (
                <div className="rounded-3xl bg-background-100 border border-background-200 p-10 md:p-12 text-center">
                  <div className="w-20 h-20 mx-auto rounded-full bg-accent-100 flex items-center justify-center mb-6">
                    <i className="ri-check-line text-accent-500 text-3xl"></i>
                  </div>
                  <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                    Message Sent!
                  </h3>
                  <p className="text-foreground-500 text-sm leading-relaxed mb-6">
                    Thank you for reaching out. Our team will get back to you within 24 business hours.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-accent-500 text-accent-600 text-sm font-semibold whitespace-nowrap hover:bg-accent-50 transition-all duration-300 cursor-pointer"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  id="contact-form"
                  className="rounded-3xl bg-background-100 border border-background-200 p-8 md:p-10"
                >
                  <div className="flex items-center gap-4 mb-8">
                    <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-send-line text-primary-500 text-2xl"></i>
                    </div>
                    <div>
                      <div className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                        Contact Us
                      </div>
                      <div className="text-foreground-500 text-sm">
                        All fields marked with * are required
                      </div>
                    </div>
                  </div>

                  <div className="space-y-5">
                    {/* Honeypot */}
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="sr-only-contact"
                    />

                    {/* Name */}
                    <div>
                      <label
                        htmlFor="contact-name"
                        className="block text-foreground-700 text-xs font-semibold tracking-wide uppercase mb-2"
                      >
                        Full Name *
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        placeholder="Enter your full name"
                        className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-foreground-900 text-sm placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label
                        htmlFor="contact-email"
                        className="block text-foreground-700 text-xs font-semibold tracking-wide uppercase mb-2"
                      >
                        Email Address *
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        placeholder="you@example.com"
                        className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-foreground-900 text-sm placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      />
                    </div>

                    {/* Phone */}
                    <div>
                      <label
                        htmlFor="contact-phone"
                        className="block text-foreground-700 text-xs font-semibold tracking-wide uppercase mb-2"
                      >
                        Phone Number
                      </label>
                      <input
                        id="contact-phone"
                        name="phone"
                        type="tel"
                        placeholder="+91 9876543210"
                        className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-foreground-900 text-sm placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      />
                    </div>

                    {/* Subject */}
                    <div>
                      <label
                        htmlFor="contact-subject"
                        className="block text-foreground-700 text-xs font-semibold tracking-wide uppercase mb-2"
                      >
                        Subject *
                      </label>
                      <select
                        id="contact-subject"
                        name="subject"
                        required
                        className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-foreground-900 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all cursor-pointer appearance-none"
                      >
                        <option value="">Select a subject</option>
                        <option value="hiring">Hiring Solutions</option>
                        <option value="partnership">Partnership Inquiry</option>
                        <option value="jobseeker">Job Seeker Support</option>
                        <option value="general">General Inquiry</option>
                      </select>
                    </div>

                    {/* Message */}
                    <div>
                      <label
                        htmlFor="contact-message"
                        className="block text-foreground-700 text-xs font-semibold tracking-wide uppercase mb-2"
                      >
                        Message *
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        rows={5}
                        maxLength={500}
                        required
                        placeholder="Tell us how we can help you..."
                        className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-foreground-900 text-sm placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                      ></textarea>
                      <p className="text-foreground-400 text-xs mt-1">Max 500 characters</p>
                    </div>

                    {/* Error */}
                    {error && (
                      <div className="flex items-center gap-2 p-4 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
                        <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                          <i className="ri-error-warning-line"></i>
                        </span>
                        {error}
                      </div>
                    )}

                    {/* Submit */}
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-primary-500 text-background-50 font-semibold text-sm whitespace-nowrap hover:bg-primary-600 transition-all duration-300 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {submitting ? (
                        <>
                          <span className="w-5 h-5 flex items-center justify-center">
                            <i className="ri-loader-4-line animate-spin"></i>
                          </span>
                          Sending...
                        </>
                      ) : (
                        <>
                          <span className="w-5 h-5 flex items-center justify-center">
                            <i className="ri-send-plane-fill text-sm"></i>
                          </span>
                          Send Message
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .sr-only-contact {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border-width: 0;
        }
      `}</style>

      <Footer />
    </main>
  );
}